<template>
	<div>
		<div class="row content-header">
			<div class="content-header-left mb-2 col-md-9 col-12">
				<div class="row breadcrumbs-top">
					<div class="col-12">
						<h2 class="content-header-title float-left pr-1 mb-0">Feed Formula</h2>
					</div>
				</div>
			</div>
			<div class="content-header-right text-md-right col-md-3 col-12 d-flex align-items-center justify-content-md-end">
				<b-button :to="{ name: addRouteName }" variant="primary">Add</b-button>
			</div>
		</div>

		<div v-if="feedGroups && feedGroups.length">
			<!-- One section per feed -->
			<div v-for="group in feedGroups" :key="'feed-' + group.feed_id" class="mb-3">
				<b-card class="mb-2">
					<div class="d-flex align-items-center">
						<h5 class="mb-0">{{ group.feed_name }}</h5>
						<b-badge class="ml-2" variant="light-primary">{{ group.formulas.length }} version(s)</b-badge>
					</div>
				</b-card>

				<!-- One card per formula (version) under a feed -->
				<b-card v-for="formula in group.formulas" :key="'formula-' + formula.id" class="mb-2">
					<!-- header row -->
					<div class="d-flex align-items-center mb-2">
						<div class="mr-auto">
							<strong>Version:</strong> {{ formula.version }}
							<span class="ml-2"><strong>Created:</strong> {{ formatDate(formula.created_at) }}</span>
							<b-badge
								v-if="formula.is_active === true || formula.is_active === false"
								:variant="formula.is_active ? 'success' : 'secondary'"
								class="ml-2"
							>
								{{ formula.is_active ? "Active" : "Inactive" }}
							</b-badge>
						</div>

						<!-- EDIT button (extreme right) -->
						<b-button
							size="sm"
							variant="outline-primary"
							:to="{ name: editRouteName, params: { id: formula.id } }"
							v-b-tooltip.hover
							title="Edit this formula"
						>
							Edit
						</b-button>
					</div>

					<!-- Items table for this formula -->
					<b-table
						:items="formula.items"
						:fields="itemFields"
						small
						responsive
						head-variant="light"
						show-empty
						empty-text="No raw materials"
						class="mb-0"
					>
						<template #cell(percentage)="data">
							{{ formatPct(data.item.percentage) }}
						</template>
					</b-table>
				</b-card>
			</div>
		</div>

		<div v-else class="d-flex flex-column align-items-center py-5">
			<b-img :src="require('@/assets/images/svg/infographics/no_data_infographic.svg')" class="mr-2" />
			<h4 class="mt-2 Secondary">No formulas found</h4>
		</div>
	</div>
</template>

<script>
import axiosIns from "@/libs/axios";
import moment from "moment";

export default {
	data() {
		return {
			userData: null,

			// grouped data -> [{ feed_id, feed_name, formulas: [{ id, version, created_at, is_active, items: [...] }] }]
			feedGroups: [],

			// per-formula items table fields
			itemFields: [
				{ key: "raw_material_name", label: "Raw material" },
				{ key: "percentage", label: "Percentage" },
				{ key: "unit_name", label: "Unit" },
			],
		};
	},
	computed: {
		// This list is reached via two different route trees depending on role
		// (super_admin -> router/routes/apps.js "co-apps-*" names, owner/admin
		// -> router/routes/company_apps.js "farm-apps-*" names), both pointing
		// at this same component. A hardcoded route name here only ever works
		// for one of the two roles, so derive the prefix from whichever name
		// this page was actually reached under, and re-use it for Add/Edit --
		// the "feed-formula-add" / "company-formula-edit" suffixes are
		// identical across both trees, only the prefix differs.
		routeNamePrefix() {
			const name = this.$route.name || "";
			return name.startsWith("co-apps-") ? "co-apps-" : "farm-apps-";
		},
		addRouteName() {
			return `${this.routeNamePrefix}feed-formula-add`;
		},
		editRouteName() {
			return `${this.routeNamePrefix}company-formula-edit`;
		},
	},
	created() {
		this.userData = JSON.parse(sessionStorage.getItem("userData") || localStorage.getItem("userData") || "null");
		this.getFeedFormula();
	},
	methods: {
		formatDate(value) {
			return value ? moment(value).format("MMMM Do YYYY") : "NA";
		},
		formatPct(val) {
			const n = parseFloat((val || "").toString());
			if (isNaN(n)) return "0%";
			const t = n.toFixed(2);
			return (t.endsWith(".00") ? String(parseInt(t, 10)) : t) + "%";
		},

		// Fetch + group by feed_id
		getFeedFormula() {
			const companyId = this.userData && this.userData.company ? this.userData.company.id : null;
			const url = `web/company/${companyId}/feed-formulas`;

			axiosIns
				.get(url)
				.then((response) => {
					// Expected response: { status: "success", data: [ ... ] }
					const list = response && response.data && response.data.data ? response.data.data : [];
					const formulas = Array.isArray(list) ? list : [];

					// Group by feed_id
					const grouped = {};
					for (let i = 0; i < formulas.length; i++) {
						const f = formulas[i] || {};
						const feedId = f.feed_id;
						const key = String(feedId);
						const feedName = (f && f.feed_name) || (f && f.feed && f.feed.name) || "Feed #" + (f ? f.feed_id : "");

						if (!grouped[key]) {
							grouped[key] = {
								feed_id: feedId,
								feed_name: feedName,
								formulas: [],
							};
						}

						// Normalize items to the three columns we show
						const items = [];
						if (f.items && f.items.length) {
							for (let j = 0; j < f.items.length; j++) {
								const it = f.items[j] || {};
								items.push({
									raw_material_name:
										it.raw_material_name ||
										(it.raw_material && it.raw_material.name) ||
										"RM #" + (it.raw_material_id != null ? it.raw_material_id : ""),
									percentage: it.percentage,
									unit_name:
										it.unit_name || (it.raw_material && it.raw_material.unit && it.raw_material.unit.name) || "",
								});
							}
						}

						grouped[key].formulas.push({
							id: f.id,
							version: f.version,
							created_at: f.created_at,
							is_active: f.is_active,
							items: items,
						});
					}

					// Convert to array + sort by feed name, then version desc (by created_at desc first)
					const groups = Object.values(grouped);
					groups.sort(function (a, b) {
						const fa = (a.feed_name || "").toLowerCase();
						const fb = (b.feed_name || "").toLowerCase();
						if (fa < fb) return -1;
						if (fa > fb) return 1;
						return 0;
					});

					for (let g = 0; g < groups.length; g++) {
						const arr = groups[g].formulas || [];
						arr.sort(function (x, y) {
							const ta = x.created_at || "";
							const tb = y.created_at || "";
							if (ta > tb) return -1;
							if (ta < tb) return 1;

							const va = Number(x.version || 0);
							const vb = Number(y.version || 0);
							if (va > vb) return -1;
							if (va < vb) return 1;
							return 0;
						});
					}

					this.feedGroups = groups;
				})
				.catch((error) => {
					// eslint-disable-next-line no-console
					console.error(error && error.response ? error.response?.data : error);
					this.feedGroups = [];
				});
		},
	},
};
</script>

<style lang="scss" scoped>
.activeClass {
	background-color: #296db4;
	color: #fff;
}
</style>